import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Download, RefreshCw, Plus, Copy } from "lucide-react";

export default function TableManager() {
  const [tables, setTables] = useState([]);
  const [num, setNum] = useState("");
  const [cap, setCap] = useState(4);
  const [slug, setSlug] = useState("");

  const load = () => api.get("/restaurant/tables").then((r) => setTables(r.data));
  useEffect(() => {
    load();
    api.get("/restaurant/me").then((r) => setSlug(r.data.slug));
  }, []);

  const nfcLink = (t) => `${window.location.origin}/?r=${slug}&table=${t.id}`;
  const copyLink = async (t) => {
    try {
      await navigator.clipboard.writeText(nfcLink(t));
      toast.success("NFC link copied");
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  const add = async () => {
    if (!num) return;
    await api.post("/restaurant/tables", { table_number: +num, capacity: +cap });
    setNum("");
    toast.success("Table added");
    load();
  };
  const del = async (t) => {
    if (!confirm(`Delete Table ${t.table_number}?`)) return;
    await api.delete(`/restaurant/tables/${t.id}`);
    load();
  };
  const regen = async (t) => {
    await api.post(`/restaurant/tables/${t.id}/regenerate-qr`);
    toast.success("QR regenerated");
    load();
  };
  const downloadAll = () => {
    // Simple print sheet
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>Tabdine — Table QR Sheet</title>
      <style>
      body{font-family:sans-serif;padding:20px}
      .tent{display:inline-block;width:280px;margin:12px;padding:16px;border:2px dashed #C84B11;border-radius:12px;text-align:center;page-break-inside:avoid}
      .brand{font-weight:700;color:#C84B11;font-size:20px}
      </style></head><body>
      ${tables.map((t) => `
        <div class="tent">
          <div class="brand">tabdine</div>
          <div style="font-size:22px;font-weight:700;margin:8px 0">Table ${t.table_number}</div>
          <img src="${t.qr_code_url}" style="width:220px;height:220px" />
          <div style="font-size:12px;color:#666;margin-top:8px">Tap or scan to order</div>
        </div>
      `).join("")}
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-5xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-dark">Tables & QR</h1>
            <div className="text-sm text-brand-dark/60">Each table has its own QR + NFC destination.</div>
          </div>
          <Button onClick={downloadAll} variant="outline" data-testid="download-all-qr">
            <Download size={14} className="mr-1" /> Download all as PDF
          </Button>
        </div>
        <div className="bg-white rounded-xl border border-brand-dark/5 p-4 mb-4 flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-xs text-brand-dark/60">Table number</label>
            <Input type="number" value={num} onChange={(e) => setNum(e.target.value)} className="w-32" data-testid="new-table-num" />
          </div>
          <div>
            <label className="text-xs text-brand-dark/60">Capacity</label>
            <Input type="number" value={cap} onChange={(e) => setCap(e.target.value)} className="w-24" />
          </div>
          <Button onClick={add} className="bg-brand-orange text-white" data-testid="add-table">
            <Plus size={14} className="mr-1" /> Add table
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {tables.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-brand-dark/5 p-3 text-center" data-testid={`table-card-${t.id}`}>
              <div className="font-heading text-lg font-bold text-brand-dark">Table {t.table_number}</div>
              <div className="text-xs text-brand-dark/60">{t.capacity} seats</div>
              <img src={t.qr_code_url} alt="qr" className="w-full aspect-square object-contain rounded my-2 bg-brand-light" />
              <div className="text-[10px] text-brand-dark/50 mb-1 truncate" title={nfcLink(t)} data-testid={`nfc-link-${t.id}`}>
                {nfcLink(t)}
              </div>
              <div className="flex gap-1 justify-center flex-wrap">
                <button onClick={() => copyLink(t)} className="text-xs px-2 py-1 rounded bg-brand-orange text-white" data-testid={`copy-nfc-${t.id}`}>
                  <Copy size={12} className="inline mr-0.5" /> NFC link
                </button>
                <a href={t.qr_code_url} download={`tabdine-table-${t.table_number}.png`} className="text-xs px-2 py-1 rounded bg-brand-dark text-brand-light">
                  <Download size={12} className="inline mr-0.5" /> QR
                </a>
                <button onClick={() => regen(t)} className="text-xs px-2 py-1 rounded border border-brand-dark/10 text-brand-dark/70" data-testid={`regen-${t.id}`}>
                  <RefreshCw size={12} className="inline mr-0.5" /> Regen
                </button>
                <button onClick={() => del(t)} className="text-xs px-2 py-1 rounded text-red-500"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </RestaurantLayout>
  );
}
